import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { startOfDay, subMonths } from 'date-fns';
import { isEqual, keyBy } from 'lodash-es';
import { FilterPicker, RadioButtonWithLabel, KeyboardDatePicker } from 'seagull';
import { SearchFilterType } from '~/components/TableFilters/types';
import { getRecurrenceFilterOptions } from '~/constants';
export { RecurrenceFilterValue } from '~/constants';
import { useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { relationshipSummaryModel, timeModel } from '~/models';
export const agreementFilterTextsPath = 'agreement';
export var FilterType = /*#__PURE__*/ function(FilterType) {
    FilterType["AgreementStatus"] = "agreementStatus";
    FilterType["EffectiveDate"] = "effectiveDate";
    FilterType["LastBillingDate"] = "lastBillingDate";
    FilterType["PaymentMethod"] = "paymentMethod";
    FilterType["HasCredit"] = "hasCredit";
    FilterType["Completion"] = "completion";
    FilterType["BillingTrigger"] = "billingTrigger";
    FilterType["Recurrence"] = "recurrence";
    FilterType["Automatic"] = "automatic";
    FilterType["ServiceNames"] = "serviceNames";
    FilterType["ServicePriceIncreaseState"] = "servicePriceIncreaseState";
    FilterType["HasAmendments"] = "hasAmendments";
    return FilterType;
}({});
export var PriceIncreaseFilterValue = /*#__PURE__*/ function(PriceIncreaseFilterValue) {
    PriceIncreaseFilterValue["None"] = "none";
    PriceIncreaseFilterValue["Automatic"] = "automatic";
    return PriceIncreaseFilterValue;
}({});
export const defaultAgreementStatusFilter = [
    relationshipSummaryModel.AgreementStatus.Active,
    relationshipSummaryModel.AgreementStatus.Exhausted
];
export const agreementStudioAgreementsArgs = {
    filters: {
        agreementStatus: defaultAgreementStatusFilter
    }
};
export function getTableFilterLabels(filterLabels) {
    return {
        ["agreementStatus"]: filterLabels.agreementStatus(),
        ["effectiveDate"]: filterLabels.effectiveDate(),
        ["lastBillingDate"]: filterLabels.lastBillingDate(),
        ["paymentMethod"]: filterLabels.paymentMethod(),
        ["hasCredit"]: filterLabels.hasCredit(),
        ["completion"]: filterLabels.completion(),
        ["recurrence"]: filterLabels.recurrence(),
        ["automatic"]: filterLabels.automatic(),
        ["billingTrigger"]: filterLabels.billingTrigger(),
        ["serviceNames"]: filterLabels.serviceNames(),
        ["servicePriceIncreaseState"]: filterLabels.servicePriceIncreaseState(),
        ["hasAmendments"]: filterLabels.hasAmendments()
    };
}
export function getTableFilterGroups(filterGroupLabels) {
    return [
        {
            label: filterGroupLabels.agreements(),
            filters: [
                "agreementStatus",
                "effectiveDate",
                "lastBillingDate",
                "paymentMethod",
                "hasCredit",
                "servicePriceIncreaseState",
                "hasAmendments"
            ]
        },
        {
            label: filterGroupLabels.services(),
            filters: [
                "serviceNames",
                "completion",
                "billingTrigger",
                "recurrence",
                "automatic"
            ]
        }
    ];
}
export function deserializeFiltersFromURL(urlParams) {
    return {
        ["agreementStatus"]: deserializeFilter("agreementStatus", urlParams.agreementStatus),
        ["effectiveDate"]: deserializeFilter("effectiveDate", urlParams.effectiveDate),
        ["lastBillingDate"]: deserializeFilter("lastBillingDate", urlParams.lastBillingDate),
        ["paymentMethod"]: deserializeFilter("paymentMethod", urlParams.paymentMethod),
        ["completion"]: deserializeFilter("completion", urlParams.completion),
        ["recurrence"]: deserializeFilter("recurrence", urlParams.recurrence),
        ["hasCredit"]: deserializeFilter("hasCredit", urlParams.hasCredit),
        ["automatic"]: deserializeFilter("automatic", urlParams.automatic),
        ["billingTrigger"]: deserializeFilter("billingTrigger", urlParams.billingTrigger),
        ["serviceNames"]: deserializeFilter("serviceNames", urlParams.serviceNames),
        ["servicePriceIncreaseState"]: deserializeFilter("servicePriceIncreaseState", urlParams.servicePriceIncreaseState),
        ["hasAmendments"]: deserializeFilter("hasAmendments", urlParams.hasAmendments)
    };
    function deserializeFilter(type, value) {
        const { deserialize } = filtersConfig[type];
        if (typeof value !== 'string') return value;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return deserialize ? deserialize(value) : value;
    }
}
function getAgreementStatusFilterOptions(labels) {
    return [
        {
            value: relationshipSummaryModel.AgreementStatus.Active,
            label: labels.active()
        },
        {
            value: relationshipSummaryModel.AgreementStatus.Exhausted,
            label: labels.completed()
        },
        {
            value: relationshipSummaryModel.AgreementStatus.Terminated,
            label: labels.terminated()
        }
    ];
}
export var HasAmendmentsFilterValue = /*#__PURE__*/ function(HasAmendmentsFilterValue) {
    HasAmendmentsFilterValue["True"] = "true";
    HasAmendmentsFilterValue["False"] = "false";
    return HasAmendmentsFilterValue;
}({});
function getHasAmendmentsFilterOptions(labels) {
    return [
        {
            value: "true",
            label: labels.yes()
        },
        {
            value: "false",
            label: labels.no()
        }
    ];
}
export function EffectiveDateFilter(props) {
    const { value, startOpen, onChange, onRemove, onInputBlur, onInputFocus } = props;
    const typedValue = value !== null && value !== void 0 ? value : {};
    const { getNow } = useIoContext();
    const { t: texts } = useLocaleKeys(agreementFilterTextsPath);
    const effectiveDateLabel = texts.filterLabels.effectiveDate();
    const hasDatesFromProps = !!(typedValue.fromDate && typedValue.toDate);
    const [selectingRange, setSelectingRange] = useState(hasDatesFromProps);
    const [fromDate, setFromDate] = useState(getInitialFromDate);
    const [toDate, setToDate] = useState(getInitialToDate);
    useEffect(()=>{
        typedValue.fromDate && setFromDate(typedValue.fromDate);
    }, [
        typedValue.fromDate
    ]);
    useEffect(()=>{
        typedValue.toDate && setToDate(typedValue.toDate);
    }, [
        typedValue.toDate
    ]);
    const label = useMemo(()=>{
        if (typedValue.fromDate && typedValue.toDate) {
            return "".concat(effectiveDateLabel, " (").concat(timeModel.formatDate(typedValue.fromDate), "-").concat(timeModel.formatDate(typedValue.toDate), ")");
        }
        return effectiveDateLabel;
    }, [
        effectiveDateLabel,
        typedValue.fromDate,
        typedValue.toDate
    ]);
    function onChangeIfDifferentValue(newValue) {
        if (!isEqual(newValue, typedValue)) {
            onChange(newValue);
        }
    }
    function getInitialFromDate() {
        return typedValue.fromDate || subMonths(getNow(), 1);
    }
    function getInitialToDate() {
        return typedValue.toDate || getNow();
    }
    return /*#__PURE__*/ _jsxs(FilterPicker, {
        removable: true,
        startOpen: startOpen,
        testId: "effective-date-filter",
        disableApply: selectingRange && fromDate && toDate && fromDate.getTime() > toDate.getTime(),
        label: label,
        title: effectiveDateLabel,
        highlightTrigger: !!(typedValue.fromDate && typedValue.toDate),
        onRemove: onRemove,
        onApply: ()=>onChangeIfDifferentValue(selectingRange ? {
                fromDate,
                toDate
            } : {}),
        onClose: (opts)=>{
            if (opts === null || opts === void 0 ? void 0 : opts.revert) {
                setSelectingRange(hasDatesFromProps);
                setFromDate(getInitialFromDate());
                setToDate(getInitialToDate());
            }
        },
        children: [
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'effective_date_all'
                },
                label: texts.dateRangeLabels.selectAll(),
                testId: "effective-date-filter-all",
                checked: !selectingRange,
                onChange: ()=>setSelectingRange(false)
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'effective_date_range'
                },
                label: texts.dateRangeLabels.specificRange(),
                testId: "effective-date-filter-range",
                checked: selectingRange,
                onChange: ()=>{
                    setSelectingRange(true);
                    const now = startOfDay(getNow());
                    if (!fromDate) {
                        setFromDate(now);
                    }
                    if (!toDate) {
                        setToDate(now);
                    }
                }
            }),
            selectingRange && fromDate && toDate && /*#__PURE__*/ _jsx(DateRangePicker, {
                testId: "effective-date-filter",
                fromDate: fromDate,
                toDate: toDate,
                onChange: (data)=>{
                    setFromDate(data.fromDate);
                    setToDate(data.toDate);
                },
                onInputFocus: onInputFocus,
                onInputBlur: onInputBlur
            })
        ]
    });
}
export var LastBillingDateType = /*#__PURE__*/ function(LastBillingDateType) {
    LastBillingDateType["None"] = "none";
    LastBillingDateType["Never"] = "never";
    LastBillingDateType["Ever"] = "ever";
    LastBillingDateType["Range"] = "range";
    return LastBillingDateType;
}({});
function getLastBillingDateOptionLabel(type, labels) {
    switch(type){
        case "ever":
            return labels.everBilled();
        case "never":
            return labels.neverBilled();
        case "range":
            return labels.specificRange();
        case "none":
            return '';
    }
}
export function LastBillingDateFilter(props) {
    const { value, startOpen, onChange, onRemove, onInputBlur, onInputFocus } = props;
    const typedValue = value !== null && value !== void 0 ? value : {
        type: "none"
    };
    const { getNow } = useIoContext();
    const { t: texts } = useLocaleKeys(agreementFilterTextsPath);
    const lastBillingDateLabel = texts.filterLabels.lastBillingDate();
    const [type, setType] = useState(typedValue.type);
    const [fromDate, setFromDate] = useState(getInitialFromDate);
    const [toDate, setToDate] = useState(getInitialToDate);
    const disableApply = fromDate && toDate && fromDate.getTime() > toDate.getTime();
    useEffect(()=>{
        setType(typedValue.type);
    }, [
        typedValue.type
    ]);
    useEffect(()=>{
        typedValue.fromDate && setFromDate(typedValue.fromDate);
    }, [
        typedValue.fromDate
    ]);
    useEffect(()=>{
        typedValue.toDate && setToDate(typedValue.toDate);
    }, [
        typedValue.toDate
    ]);
    const label = useMemo(()=>{
        switch(typedValue.type){
            case "ever":
            case "never":
                return "".concat(lastBillingDateLabel, " (").concat(getLastBillingDateOptionLabel(typedValue.type, texts.lastBillingDateOptions), ")");
            case "range":
                return "".concat(lastBillingDateLabel, " (").concat(timeModel.formatDate(typedValue.fromDate), "-").concat(timeModel.formatDate(typedValue.toDate), ")");
            case "none":
                return lastBillingDateLabel;
        }
    }, [
        lastBillingDateLabel,
        texts,
        typedValue
    ]);
    function onChangeIfDifferentValue(newValue) {
        if (!isEqual(newValue, typedValue)) {
            onChange(newValue);
        }
    }
    function getInitialFromDate() {
        return typedValue.fromDate || subMonths(getNow(), 1);
    }
    function getInitialToDate() {
        return typedValue.toDate || getNow();
    }
    return /*#__PURE__*/ _jsxs(FilterPicker, {
        removable: true,
        startOpen: startOpen,
        testId: "last-billing-date-filter",
        disableApply: disableApply,
        label: label,
        title: lastBillingDateLabel,
        highlightTrigger: typedValue.type !== "none",
        onRemove: onRemove,
        onApply: ()=>{
            if (type === "range") {
                fromDate && toDate && onChangeIfDifferentValue({
                    type,
                    fromDate,
                    toDate
                });
            } else {
                onChangeIfDifferentValue({
                    type
                });
            }
        },
        onClose: (opts)=>{
            if (opts === null || opts === void 0 ? void 0 : opts.revert) {
                setType(value.type);
                setFromDate(getInitialFromDate());
                setToDate(getInitialToDate());
            }
        },
        children: [
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'last_billing_date_all'
                },
                label: texts.dateRangeLabels.selectAll(),
                testId: "last-billing-date-filter-all",
                checked: type === "none",
                onChange: ()=>setType("none")
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'last_billing_date_ever'
                },
                label: getLastBillingDateOptionLabel("ever", texts.lastBillingDateOptions),
                testId: "last-billing-date-filter-ever",
                checked: type === "ever",
                onChange: ()=>setType("ever")
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'last_billing_date_never'
                },
                label: getLastBillingDateOptionLabel("never", texts.lastBillingDateOptions),
                testId: "last-billing-date-filter-never",
                checked: type === "never",
                onChange: ()=>setType("never")
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'last_billing_date_range'
                },
                label: getLastBillingDateOptionLabel("range", texts.lastBillingDateOptions),
                testId: "last-billing-date-filter-range",
                checked: type === "range",
                onChange: ()=>{
                    setType("range");
                    const now = startOfDay(getNow());
                    if (!fromDate) {
                        setFromDate(now);
                    }
                    if (!toDate) {
                        setToDate(now);
                    }
                }
            }),
            type === "range" && fromDate && toDate && /*#__PURE__*/ _jsx(DateRangePicker, {
                testId: "last-billing-date-filter",
                fromDate: fromDate,
                toDate: toDate,
                onChange: (data)=>{
                    setFromDate(data.fromDate);
                    setToDate(data.toDate);
                },
                onInputFocus: onInputFocus,
                onInputBlur: onInputBlur
            })
        ]
    });
}
export var PaymentMethodFilterValue = /*#__PURE__*/ function(PaymentMethodFilterValue) {
    PaymentMethodFilterValue["CreditCard"] = "credit";
    PaymentMethodFilterValue["BankAccount"] = "bank";
    PaymentMethodFilterValue["VerificationInitiated"] = "verificationInitiated";
    PaymentMethodFilterValue["VerificationFailed"] = "verificationFailed";
    PaymentMethodFilterValue["NotSet"] = "notSet";
    return PaymentMethodFilterValue;
}({});
function getPaymentMethodFilterOptions(labels) {
    return [
        {
            value: "credit",
            label: labels.card()
        },
        {
            value: "bank",
            label: labels.bankAccount()
        },
        {
            value: "verificationInitiated",
            label: labels.manualVerificationInitiated()
        },
        {
            value: "verificationFailed",
            label: labels.manualVerificationFailed()
        },
        {
            value: "notSet",
            label: labels.notSet()
        }
    ];
}
export var CompletionFilterValue = /*#__PURE__*/ function(CompletionFilterValue) {
    CompletionFilterValue["Completed"] = "completed";
    CompletionFilterValue["NotCompleted"] = "notCompleted";
    return CompletionFilterValue;
}({});
function getCompletionFilterOptions(labels) {
    return [
        {
            value: "notCompleted",
            label: labels.notCompleted()
        },
        {
            value: "completed",
            label: labels.completed()
        }
    ];
}
export var HasCreditFilterValue = /*#__PURE__*/ function(HasCreditFilterValue) {
    HasCreditFilterValue["Yes"] = "yes";
    HasCreditFilterValue["No"] = "no";
    return HasCreditFilterValue;
}({});
function getHasCreditFilterOptions(labels) {
    return [
        {
            value: "yes",
            label: labels.yes()
        },
        {
            value: "no",
            label: labels.no()
        }
    ];
}
export var AutomaticFilterValue = /*#__PURE__*/ function(AutomaticFilterValue) {
    AutomaticFilterValue["Active"] = "active";
    AutomaticFilterValue["Paused"] = "paused";
    return AutomaticFilterValue;
}({});
function getAutomaticFilterOptions(labels) {
    return [
        {
            value: "active",
            label: labels.active()
        },
        {
            value: "paused",
            label: labels.paused()
        }
    ];
}
export var BillingTriggerFilterValue = /*#__PURE__*/ function(BillingTriggerFilterValue) {
    BillingTriggerFilterValue["Automatic"] = "automatic";
    BillingTriggerFilterValue["Manual"] = "manual";
    return BillingTriggerFilterValue;
}({});
function getBillingTriggerFilterOptions(labels) {
    return [
        {
            value: "automatic",
            label: labels.automatic()
        },
        {
            value: "manual",
            label: labels.manual()
        }
    ];
}
function DateRangePicker(props) {
    const { fromDate, toDate, testId, onChange, onInputBlur, onInputFocus } = props;
    const { t: texts } = useLocaleKeys(agreementFilterTextsPath);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(KeyboardDatePicker, {
                bi: {
                    name: 'agreement_date_from'
                },
                label: texts.dateRangeLabels.from(),
                testId: testId && "".concat(testId, "-from"),
                value: fromDate,
                maxDate: toDate,
                onChange: (date)=>date && onChange && onChange({
                        fromDate: date,
                        toDate
                    }),
                onFocus: onInputFocus,
                onBlur: onInputBlur
            }),
            /*#__PURE__*/ _jsx(KeyboardDatePicker, {
                bi: {
                    name: 'agreement_date_to'
                },
                label: texts.dateRangeLabels.to(),
                testId: testId && "".concat(testId, "-to"),
                value: toDate,
                minDate: fromDate,
                onChange: (date)=>date && onChange && onChange({
                        fromDate,
                        toDate: date
                    }),
                onFocus: onInputFocus,
                onBlur: onInputBlur
            })
        ]
    });
}
function getPriceIncreaseFilterOptions(labels) {
    return [
        {
            value: "automatic",
            label: labels.included()
        },
        {
            value: "none",
            label: labels.notIncluded()
        }
    ];
}
function getMultiSelectLabelsByFilterType(texts, recurrencePeriodsTexts) {
    return {
        ["agreementStatus"]: keyBy(getAgreementStatusFilterOptions(texts.agreementStatusOptions), 'value'),
        ["paymentMethod"]: keyBy(getPaymentMethodFilterOptions(texts.paymentMethodOptions), 'value'),
        ["hasCredit"]: keyBy(getHasCreditFilterOptions(texts.hasCreditOptions), 'value'),
        ["completion"]: keyBy(getCompletionFilterOptions(texts.completionOptions), 'value'),
        ["recurrence"]: keyBy(getRecurrenceFilterOptions(recurrencePeriodsTexts, {
            onDemand: true
        }), 'value'),
        ["automatic"]: keyBy(getAutomaticFilterOptions(texts.automaticOptions), 'value'),
        ["billingTrigger"]: keyBy(getBillingTriggerFilterOptions(texts.billingTriggerOptions), 'value'),
        ["servicePriceIncreaseState"]: keyBy(getPriceIncreaseFilterOptions(texts.priceIncreaseOptions), 'value'),
        ["hasAmendments"]: keyBy(getHasAmendmentsFilterOptions(texts.hasAmendmentsOptions), 'value')
    };
}
function formatMultiSelectDescription(values, labelByValue) {
    if (values.length === 0) return undefined;
    if (!labelByValue) return values.join(', ');
    const labels = values.map((v)=>{
        var _ref;
        var _labelByValue_v;
        return (_ref = (_labelByValue_v = labelByValue[v]) === null || _labelByValue_v === void 0 ? void 0 : _labelByValue_v.label) !== null && _ref !== void 0 ? _ref : v;
    });
    return labels.join(', ');
}
export function formatFilterDescription(params) {
    const { filterType, value, texts, recurrencePeriodsTexts } = params;
    switch(filterType){
        case "effectiveDate":
            {
                const typed = value;
                if (!typed.fromDate || !typed.toDate) return undefined;
                return "".concat(timeModel.formatDate(typed.fromDate), " - ").concat(timeModel.formatDate(typed.toDate));
            }
        case "lastBillingDate":
            {
                const typed = value;
                switch(typed.type){
                    case "ever":
                        return getLastBillingDateOptionLabel("ever", texts.lastBillingDateOptions);
                    case "never":
                        return getLastBillingDateOptionLabel("never", texts.lastBillingDateOptions);
                    case "range":
                        return "".concat(timeModel.formatDate(typed.fromDate), " - ").concat(timeModel.formatDate(typed.toDate));
                    case "none":
                        return undefined;
                }
                break;
            }
        case "serviceNames":
            return formatMultiSelectDescription(value);
        default:
            {
                const labelsByType = getMultiSelectLabelsByFilterType(texts, recurrencePeriodsTexts);
                return formatMultiSelectDescription(value, labelsByType[filterType]);
            }
    }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSimpleMultiSelectFilterConfig(type) {
    return {
        hasValue (v) {
            return v.length > 0;
        },
        getEmptyValue () {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return [];
        },
        getRequestParam (v) {
            return {
                [type]: v.length ? v.join(',') : undefined
            };
        }
    };
}
const effectiveDateConfig = {
    hasValue (effectiveDate) {
        return !!(effectiveDate.fromDate && effectiveDate.toDate);
    },
    getEmptyValue () {
        return {};
    },
    getRequestParam (effectiveDate) {
        return {
            fromDate: effectiveDate.fromDate && timeModel.toDatestampString(effectiveDate.fromDate),
            toDate: effectiveDate.toDate && timeModel.toDatestampString(effectiveDate.toDate)
        };
    },
    serialize (param) {
        let { fromDate, toDate } = param;
        return fromDate && toDate ? "".concat(timeModel.toDatestampString(fromDate), ",").concat(timeModel.toDatestampString(toDate)) : '';
    },
    deserialize (str) {
        const splitted = str.split(',');
        return splitted.length === 2 ? {
            fromDate: timeModel.fromDatestampString(splitted[0]),
            toDate: timeModel.fromDatestampString(splitted[1])
        } : {};
    }
};
const lastBillingDateConfig = {
    hasValue (value) {
        return value.type !== "none";
    },
    getEmptyValue () {
        return {
            type: "none"
        };
    },
    getRequestParam (lastBillingDate) {
        const isLastBilledWithinRange = lastBillingDate.type === "range";
        return {
            lastBilled: lastBillingDate.type !== "none" ? lastBillingDate.type : undefined,
            lastBilledFrom: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.fromDate) : undefined,
            lastBilledTo: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.toDate) : undefined
        };
    },
    serialize (data) {
        if (data.type === "none") {
            return '';
        }
        if (data.type === "range") {
            return "".concat(timeModel.toDatestampString(data.fromDate), ",").concat(timeModel.toDatestampString(data.toDate));
        }
        return data.type;
    },
    deserialize (lastBilled) {
        if (!lastBilled) {
            return {
                type: "none"
            };
        }
        if (lastBilled === "ever" || lastBilled === "never") {
            return {
                type: lastBilled
            };
        }
        const dates = lastBilled.split(',');
        if (dates.length !== 2) {
            return {
                type: "none"
            };
        }
        const fromDate = timeModel.fromDatestampString(dates[0]);
        const toDate = timeModel.fromDatestampString(dates[1]);
        return {
            type: "range",
            fromDate,
            toDate
        };
    }
};
export const filtersConfig = {
    ["effectiveDate"]: effectiveDateConfig,
    ["lastBillingDate"]: lastBillingDateConfig,
    ["agreementStatus"]: getSimpleMultiSelectFilterConfig("agreementStatus"),
    ["paymentMethod"]: getSimpleMultiSelectFilterConfig("paymentMethod"),
    ["completion"]: getSimpleMultiSelectFilterConfig("completion"),
    ["recurrence"]: getSimpleMultiSelectFilterConfig("recurrence"),
    ["hasCredit"]: getSimpleMultiSelectFilterConfig("hasCredit"),
    ["automatic"]: getSimpleMultiSelectFilterConfig("automatic"),
    ["billingTrigger"]: getSimpleMultiSelectFilterConfig("billingTrigger"),
    ["serviceNames"]: getSimpleMultiSelectFilterConfig("serviceNames"),
    ["servicePriceIncreaseState"]: getSimpleMultiSelectFilterConfig("servicePriceIncreaseState"),
    ["hasAmendments"]: getSimpleMultiSelectFilterConfig("hasAmendments")
};
export function serializeFilter(type, value) {
    const { serialize } = filtersConfig[type];
    return serialize ? serialize(value) : value;
}
export function makeTableFilterConfig(params) {
    const { serviceNameOptions, onDemandEnabled, texts, recurrencePeriodsTexts } = params;
    return {
        ["agreementStatus"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getAgreementStatusFilterOptions(texts.agreementStatusOptions)
        },
        ["effectiveDate"]: {
            type: SearchFilterType.CustomFilter,
            removable: true,
            component: EffectiveDateFilter,
            initialValue: {}
        },
        ["lastBillingDate"]: {
            type: SearchFilterType.CustomFilter,
            removable: true,
            component: LastBillingDateFilter,
            initialValue: {
                type: "none"
            }
        },
        ["paymentMethod"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getPaymentMethodFilterOptions(texts.paymentMethodOptions)
        },
        ["hasCredit"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getHasCreditFilterOptions(texts.hasCreditOptions)
        },
        ["completion"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getCompletionFilterOptions(texts.completionOptions)
        },
        ["recurrence"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getRecurrenceFilterOptions(recurrencePeriodsTexts, {
                onDemand: onDemandEnabled
            })
        },
        ["automatic"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getAutomaticFilterOptions(texts.automaticOptions)
        },
        ["billingTrigger"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getBillingTriggerFilterOptions(texts.billingTriggerOptions)
        },
        ["serviceNames"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            withSearchbox: true,
            ellipsis: true,
            options: serviceNameOptions
        },
        ["servicePriceIncreaseState"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getPriceIncreaseFilterOptions(texts.priceIncreaseOptions)
        },
        ["hasAmendments"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getHasAmendmentsFilterOptions(texts.hasAmendmentsOptions)
        }
    };
}
